/**
 * Wo ein Projekt oder ein Dataspace liegt — persönlich oder in genau einer Abteilung — und was
 * darüber gesagt wird, wo immer er angezeigt, gewählt, verschoben oder zugeordnet wird: Designer,
 * Operator und Kontrollzentrum verwenden diese Texte.
 *
 * `errors` ist nach den Fehlercodes des Servers benannt und wird dynamisch nachgeschlagen
 * (`utils/spaceErrors.ts`); diese Schlüsselnamen sind also ein Vertrag mit der API und nicht frei
 * wählbar.
 */
export default {
	spaces: {
		personal: "Persönlich",
		kind: {
			project: "Projekt",
			dataspace: "Dataspace",
		},
		picker: {
			label: "Ablageort",
			help: "Persönlich: Zugriff haben nur die Personen, die Sie hinzufügen. In einer Abteilung: Zugriff haben deren Mitglieder über die Rollen, die die Abteilung zuordnet.",
		},
		filter: {
			label: "Abteilung",
			all: "Alle",
			personal: "Persönlich",
		},
		location: {
			title: "Abteilung",
			personal: {
				project:
					"Dieses Projekt ist persönlich: Zugriff haben nur die Personen, die ihm hinzugefügt wurden. Verschieben Sie es in eine Abteilung, damit deren Mitglieder Zugriff erhalten.",
				dataspace:
					"Dieser Dataspace ist persönlich: Zugriff haben nur die Personen, die ihm hinzugefügt wurden. Verschieben Sie ihn in eine Abteilung, damit deren Mitglieder Zugriff erhalten.",
			},
			inDepartment: {
				project:
					"Dieses Projekt gehört zu {department}. Die Abteilungsmitglieder greifen über den Abteilungszugriff darauf zu, die Abteilungseigentümer verwalten es.",
				dataspace:
					"Dieser Dataspace gehört zu {department}. Die Abteilungsmitglieder greifen über den Abteilungszugriff darauf zu, die Abteilungseigentümer verwalten ihn.",
			},
			needsDelete: {
				project:
					"Ein Projekt zu verschieben heißt, es abzugeben, und dazu braucht es das Recht, es zu löschen. Dieses Recht haben Sie nicht.",
				dataspace:
					"Einen Dataspace zu verschieben heißt, ihn abzugeben, und dazu braucht es das Recht, ihn zu löschen. Dieses Recht haben Sie nicht.",
			},
			cannotLeave:
				"Einen Space aus {department} herauszunehmen erfordert das Recht, die Spaces von {department} zu verwalten. Ihre Rolle dort enthält dieses Recht nicht.",
			nowhereToGo:
				"Sie gehören keiner Abteilung an, in die Sie dies verschieben dürfen. Dafür ist das Recht erforderlich, Spaces in eine Abteilung zu verschieben.",
			moveTo: "In Abteilung verschieben…",
			makePersonal: "Persönlich machen",
		},
		move: {
			titleTo: "{name} in eine Abteilung verschieben",
			titlePersonal: "{name} persönlich machen",
			consequenceIn:
				"Der Abteilungszugriff wird zurückgesetzt: Die Eigentümer der Abteilung werden zu Admins, und niemand sonst hat über die Abteilung Zugriff, bis Sie weitere Rollen zuordnen. Von Hand hinzugefügte Personen behalten ihren Zugriff.",
			consequenceOut:
				"Über die Abteilung hat niemand mehr Zugriff, und der Abteilungszugriff wird gelöscht. Ist keine von Hand hinzugefügte Person Admin, werden Sie Admin.",
			target: "Abteilung",
			pick: "Abteilung wählen",
			none: "Sie gehören keiner Abteilung an, in die Sie Spaces verschieben dürfen.",
			confirmTo: "Verschieben",
			confirmPersonal: "Persönlich machen",
			movedTo: "{name} liegt jetzt in {department}.",
			madePersonal: "{name} ist jetzt persönlich.",
		},
		access: {
			title: "Abteilungszugriff",
			intro: {
				project:
					"Die Mitglieder von {department} haben in diesem Projekt die Rolle, die ihrer Abteilungsrolle zugeordnet ist. Von Hand hinzugefügte Personen behalten die Rolle, die sie erhalten haben.",
				dataspace:
					"Die Mitglieder von {department} haben in diesem Dataspace die Rolle, die ihrer Abteilungsrolle zugeordnet ist. Von Hand hinzugefügte Personen behalten die Rolle, die sie erhalten haben.",
			},
			departmentRole: "Abteilungsrolle",
			gets: "Rolle im {kind}",
			everyone: "Jedes Mitglied",
			locked: "Feste Zeile",
			lockedHint:
				"Die Eigentümer der Abteilung verwalten immer alles, was zur Abteilung gehört. Diese Zeile lässt sich daher weder ändern noch entfernen.",
			add: "Zuordnung hinzufügen",
			remove: "Zuordnung für {role} entfernen",
			empty: "Über die Abteilung haben nur deren Eigentümer Zugriff.",
			saved: "Abteilungszugriff gespeichert.",
			changedElsewhere:
				"Jemand anderes hat diesen Abteilungszugriff geändert, während Sie ihn bearbeitet haben. Ihre Änderungen bleiben erhalten; verwerfen Sie sie, um die Änderungen der anderen Person zu sehen.",
			readOnly: "Sie dürfen diesen Abteilungszugriff lesen, aber nicht ändern.",
		},
		addFromDepartment: {
			action: "Aus Abteilung hinzufügen",
			title: "Aus {department} hinzufügen",
			note: {
				project:
					"Mitglieder von {department}, die noch nicht in diesem Projekt sind. Nur Mitglieder von {department} können darin sein: Alle anderen treten zuerst der Abteilung bei.",
				dataspace:
					"Mitglieder von {department}, die noch nicht in diesem Dataspace sind. Nur Mitglieder von {department} können darin sein: Alle anderen treten zuerst der Abteilung bei.",
			},
			search: "Nach Name oder E-Mail suchen…",
			label: "Mitglieder von {department}",
			empty: {
				project: "Alle Mitglieder von {department} sind bereits in diesem Projekt.",
				dataspace: "Alle Mitglieder von {department} sind bereits in diesem Dataspace.",
			},
			role: "Rolle",
			submit: "Hinzufügen | Hinzufügen | {n} Personen hinzufügen",
			added: "{name} wurde hinzugefügt. | {n} Personen wurden hinzugefügt.",
		},
		errors: {
			mapping_locked:
				"Die Eigentümer der Abteilung erhalten immer die Admin-Rolle. Diese Zeile lässt sich daher nicht ändern.",
			not_in_department:
				"Ein Space liegt nicht in der Abteilung, in der er erwartet wurde. Vielleicht wurde er gerade verschoben: Laden Sie die Seite neu und versuchen Sie es erneut.",
			role_in_use:
				"Ein Abteilungszugriff vergibt diese Rolle noch an Personen. Entfernen oder ändern Sie zuerst diese Zuordnung.",
			space_needs_admin:
				"Ein Space hätte als persönlicher Space keinen eigenen Admin mehr. Fügen Sie ihm zuerst einen hinzu, auf seiner Seite im Kontrollzentrum.",
			invalid_role:
				"Diese Rolle gibt es in der Abteilung oder im Space nicht mehr. Laden Sie die Seite neu und versuchen Sie es erneut.",
			duplicate_mapping:
				"Eine Abteilungsrolle kann nur einer Rolle des Spaces zugeordnet werden.",
			no_spaces: "Wählen Sie mindestens ein Projekt oder einen Dataspace.",
			role_required: "Die Standardabteilung braucht die Rolle, die sie allen gibt.",
			space_not_found:
				"Ein Space existiert nicht oder ist für Sie nicht mehr zugänglich. Es wurde nichts verschoben.",
			missing_entitlement:
				"Ihre Rolle erlaubt das nicht. Zum Verschieben eines Spaces braucht es das Recht, ihn zu löschen, und das passende Recht in den beteiligten Abteilungen; zum Anlegen eines Spaces braucht es das Recht, in der Abteilung Spaces anzulegen.",
			invitation_required:
				"Aus dieser Liste lassen sich nur Mitglieder der Abteilung hinzufügen. Alle anderen werden zuerst in die Abteilung eingeladen.",
			not_a_department_member:
				"Nur Mitglieder der Abteilung des Space können darin sein. Die Person tritt zuerst der Abteilung bei.",
		},
		/** Die Mitgliederliste eines Space: alle, die ihn erreichen, und wie. */
		members: {
			viaDepartment: "über {department}",
			alsoViaDepartment: "zusätzlich {roles} über {department}",
			notInDepartment: "kein Mitglied von {department}",
			noAccess: "Kein Zugriff",
			noAccessBand: "Nicht in der Abteilung",
			removeKeepsDepartment:
				"Die eigene Mitgliedschaft wird entfernt. Was {department} der Person hier gibt, bleibt.",
		},
	},
};
